import React, {
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { Box, Paper, SxProps, Theme, Typography } from '@mui/material';
import { Message, MessageStatus, Sender } from 'services/conversations/types';
import { SimulationMessage } from 'services/campaign/types';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Tooltip } from 'components/Tooltip';
import twilioErrorCodes from 'core/config/constants/twilioErrorCode.json';
import { MediaPreview } from './Media/MediaPreview';
import { MediaAudioPlayer } from './Media/MediaAudioPlayer';
import { MediaType } from './Media/types';
import { MediaDownload } from './Media/MediaDownload';
import { formatDate } from 'utils/datetime';

type Props = {
  messages: SimulationMessage[] | Message[];
  prospectDisplayName: string;
  agentDisplayName: string;
  maxHeight?: number | string;
  preventAutoScrolling?: boolean;
  hideDeliveryTickExpectInitial?: boolean;
  flaggedMessageIds?: string[];
};

export type MessagesListRef = {
  scrollToMessage: (messageId: string) => void;
};

const container: SxProps = {
  p: '28px 32px',
  background: 'white'
};

const messagesPaper: SxProps<Theme> = {
  overflow: 'hidden',
  position: 'absolute',
  inset: 0,
  overflowY: 'auto',
  border: '1px solid',
  borderColor: 'customGrey.dark'
};

const messageHeaderContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  mb: '6px'
};

const messageMetaText: SxProps<Theme> = {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: 'customGrey.dark'
};

const messageText: SxProps<Theme> = {
  fontSize: '16px',
  lineHeight: '26px',
  color: 'customGrey.dark'
};

const mediaContainer: SxProps = {
  mt: 1
};

const messageContainer: SxProps = {
  maxWidth: '70%'
};

const messageBodyContainer: SxProps = {
  wordBreak: 'break-word',
  display: 'flex',
  position: 'relative',
  borderRadius: '8px 8px 8px 8px',
  padding: '10px 14px',
  alignItems: 'flex-start',
  minWidth: 180,
  transition:
    'background-color 0.3s ease, border-color 0.3s ease, padding 0.3s ease'
};

const messageFooterContainer: SxProps = {
  display: 'flex',
  alignSelf: 'flex-end',
  ml: '8px'
};

const pulseAnimation: SxProps<Theme> = {
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(146, 77, 0, 0.4)' },
    '70%': { boxShadow: '0 0 0 10px rgba(146, 77, 0, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(146, 77, 0, 0)' }
  },
  animation: 'pulse 0.6s ease-out 2'
};

const flaggedMessageBodyStyle: SxProps<Theme> = {
  backgroundColor: 'warningV2.light',
  border: '1px solid',
  borderColor: 'error.main',
  borderTopLeftRadius: 0,
  paddingLeft: '38px'
};

function formatMessageBody(body: string | ReactElement): ReactElement | null {
  if (typeof body === 'string') {
    const segments = body.split(/(\*.*?\*)/g); // Split the string by asterisks but keep the asterisks and enclosed text

    const bodyFormatted = segments.map((segment, index) => {
      // For even indices, return the text as-is
      if (index % 2 === 0) return segment;

      // For odd indices, remove the asterisks and return a strong (bold) element
      const textWithoutAsterisks = segment.slice(1, -1);
      return React.createElement(
        'strong',
        { key: index },
        textWithoutAsterisks
      );
    });
    return <>{bodyFormatted}</>;
  }

  return body;
}

const MessageContainer = ({
  message,
  prospectDisplayName,
  agentDisplayName,
  hideDeliveryTick,
  isFlagged,
  isPulsing,
  ref
}: {
  message: SimulationMessage | Message;
  agentDisplayName: string;
  prospectDisplayName: string;
  status?: MessageStatus;
  hideDeliveryTick?: boolean;
  isFlagged?: boolean;
  isPulsing?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const isAgent =
    message.sender === Sender.AGENT || message.sender === Sender.USER;
  const name = isAgent ? agentDisplayName : prospectDisplayName;

  const additionalContainerStyle: SxProps = isAgent
    ? { justifyContent: 'flex-end' }
    : { justifyContent: 'flex-start' };

  const additionalBodyStyle: SxProps<Theme> = (() => {
    if (isFlagged && !isAgent) {
      return flaggedMessageBodyStyle;
    }
    return isAgent
      ? {
          backgroundColor: 'customGrey.medium',
          borderTopRightRadius: 0
        }
      : {
          backgroundColor: 'customGrey.light',
          borderTopLeftRadius: 0
        };
  })();

  const icon = useMemo(() => {
    if (hideDeliveryTick) return null;

    if ('status' in message) {
      if (message.status === MessageStatus.SENT)
        return (
          <CheckIcon fontSize="small" sx={{ color: 'customGrey.light' }} />
        );

      if (message.status === MessageStatus.SENT_SUCCESS)
        return (
          <DoneAllIcon fontSize="small" sx={{ color: 'customGrey.light' }} />
        );

      if (message.status === MessageStatus.READ)
        return <DoneAllIcon fontSize="small" sx={{ color: 'primary.main' }} />;

      if (message.status === MessageStatus.SENT_FAILED) {
        // Channel-aware tooltip: SMS/WhatsApp failures carry a
        // twilioErrorCode (looked up in twilioErrorCode.json for the
        // human-readable reason). Email failures don't — currently no
        // SES bounce reason is persisted on the Message row, so we
        // fall back to a channel-specific generic. Either way we
        // always render a tooltip so the error icon is never bare.
        const tooltipTitle = (() => {
          if ('twilioErrorCode' in message && message.twilioErrorCode) {
            return (
              // @ts-expect-error: twilioErrorCodes is a JSON object with string keys that represent the error code
              twilioErrorCodes[message.twilioErrorCode] ||
              'Message could not be delivered'
            );
          }

          if ('emailSubjectLine' in message) {
            return 'Email could not be delivered';
          }

          return 'Message could not be delivered';
        })();

        return (
          <Tooltip title={tooltipTitle}>
            <ErrorIcon fontSize="small" color="error" />
          </Tooltip>
        );
      }

      return null;
    }

    return null;
  }, [hideDeliveryTick, JSON.stringify(message)]);

  const isMedia = 'media' in message && !!message.media;

  const mediaType = useMemo<MediaType | undefined>(() => {
    if (isMedia) {
      if (!message.media) return;
      const { contentType } = message.media;

      if (contentType.startsWith('application/pdf')) return MediaType.PDF;
      if (contentType.startsWith('audio/')) return MediaType.AUDIO;
      if (contentType.startsWith('video/')) return MediaType.VIDEO;
      if (contentType.startsWith('text/') || contentType.includes('document'))
        return MediaType.DOCUMENT;
      return MediaType.OTHER;
    }
  }, [isMedia]);

  const fileName = useMemo<string | undefined>(() => {
    if (!isMedia) return;
    if (mediaType === MediaType.AUDIO) return;
    return message.media?.classifiedDocumentType || message.media?.fileName;
  }, [isMedia, mediaType]);

  return (
    <Box
      sx={[
        {
          mb: 2,
          display: 'flex',
          ...additionalContainerStyle
        },
        isPulsing ? pulseAnimation : {}
      ]}
      ref={ref}
    >
      <Box sx={messageContainer}>
        <Box sx={messageHeaderContainer}>
          <Typography sx={messageMetaText}>{name}</Typography>
          <Typography sx={messageMetaText}>
            {formatDate(
              'deliveredAt' in message
                ? message?.deliveredAt || message?.updatedAt
                : message?.updatedAt || message?.createdAt,
              { showTime: true }
            )}
          </Typography>
        </Box>
        <Box
          sx={
            {
              ...messageBodyContainer,
              ...additionalBodyStyle
            } as SxProps<Theme>
          }
        >
          {isFlagged && !isAgent && (
            <WarningAmberIcon
              sx={{
                fontSize: '18px',
                color: 'warningV2.dark',
                position: 'absolute',
                left: '12px',
                top: '12px',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'scale(0.8)' },
                  '100%': { opacity: 1, transform: 'scale(1)' }
                },
                animation: 'fadeIn 0.3s ease'
              }}
            />
          )}
          {isMedia && message.media && mediaType && (
            <MediaPreview fileName={fileName} mediaType={mediaType} />
          )}
          <Box>
            {!isMedia &&
              'emailSubjectLine' in message &&
              message.emailSubjectLine && (
                <Typography sx={messageText} mb={2}>
                  Subject: {message.emailSubjectLine}
                </Typography>
              )}
            <Typography sx={messageText} whiteSpace="pre-line">
              {formatMessageBody(message.body)}
            </Typography>
          </Box>
          {message.sender !== Sender.PROSPECT && (
            <Box sx={messageFooterContainer}>{icon}</Box>
          )}
        </Box>
        {isMedia && message.media && (
          <Box sx={mediaContainer}>
            {mediaType === MediaType.AUDIO ? (
              <MediaAudioPlayer twilioMediaSid={message.media.twilioMediaSid} />
            ) : (
              <MediaDownload
                fileName={message.media.classifiedDocumentType}
                messageId={message.id}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const MessagesList = forwardRef<MessagesListRef, Props>(
  (
    { messages, prospectDisplayName, agentDisplayName, flaggedMessageIds = [] },
    ref
  ) => {
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const paperContainerRef = useRef<HTMLDivElement | null>(null);
    const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const [pulsingMessageId, setPulsingMessageId] = useState<string | null>(
      null
    );

    const scrollToMessage = useCallback((messageId: string) => {
      const messageElement = messageRefs.current.get(messageId);
      if (messageElement && paperContainerRef.current) {
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setPulsingMessageId(messageId);
        setTimeout(() => setPulsingMessageId(null), 1200);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        scrollToMessage
      }),
      [scrollToMessage]
    );

    useEffect(() => {
      const scrollToBottom = () => {
        const paperContainer = paperContainerRef.current;
        if (paperContainer) {
          paperContainer.scrollTo({
            top: paperContainer.scrollHeight,
            behavior: 'auto'
          });
        }
      };

      if (messages?.length > 1) {
        scrollToBottom();
      }
    }, [messages.length]);

    const flaggedMessageIdsSet = useMemo(
      () => new Set(flaggedMessageIds),
      [flaggedMessageIds]
    );

    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          width: '100%',
          minHeight: '60vh'
        }}
      >
        <Paper ref={paperContainerRef} sx={messagesPaper}>
          <Box sx={container}>
            {messages.map((message) => {
              const isFlagged = flaggedMessageIdsSet.has(message.id);
              const isPulsing = pulsingMessageId === message.id;

              return (
                <Box
                  key={message.id}
                  id={`message-${message.id}`}
                  ref={(el: HTMLDivElement | null) => {
                    if (el) {
                      messageRefs.current.set(message.id, el);
                    } else {
                      messageRefs.current.delete(message.id);
                    }
                  }}
                >
                  <MessageContainer
                    message={message}
                    prospectDisplayName={prospectDisplayName}
                    agentDisplayName={agentDisplayName}
                    isFlagged={isFlagged}
                    isPulsing={isPulsing}
                  />
                </Box>
              );
            })}
            <div
              ref={messagesEndRef}
              style={{
                // This div is used to scroll to the bottom of the messages list
                height: 20
              }}
            />
          </Box>
        </Paper>
      </Box>
    );
  }
);
